import { Mapping, useUpdateDescription, useUpdateSampleValue } from 'api/MappingsAPI';
import { useCustomEventsQueryContext } from 'modules/customEvents/contexts';
import { FIELD_PROPERTY_DETAILS } from 'modules/dataDictionary/constants';
import {
  useCustomEventDetailsContext,
  usePurchaseMappingsContext,
  useUserMappingsContext,
} from 'modules/dataDictionary/contexts';
import { FieldProperty, MappingFieldValues } from 'modules/dataDictionary/types';
import { GenericDataType } from 'modules/domains';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { ApiError } from 'services/api';
import { BUTTON_SIZES, Button } from 'ui/button';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { Body2, colors } from 'ui/theme';
import { useModalContext } from 'utils/contexts/modal';
import { ControlledInputField } from './ControlledInputField';

export const EDIT_PROPERTY_MODAL_TEXT = {
  formId: 'edit-field-modal-form',
  modalId: 'edit-field-modal',
  saveButtonText: 'Save',
};

type EditPropertyModalProps = {
  field?: Mapping;
  fieldProperty: FieldProperty;
};

export const EditPropertyModal = ({ field, fieldProperty }: EditPropertyModalProps) => {
  const dispatch = useDispatch();
  const { hideModal } = useModalContext();
  const { project } = useCustomEventsQueryContext();
  const { customEventName = '' } = useParams<{ customEventName: string }>();
  const { refetchUserMappings } = useUserMappingsContext();
  const { refetchPurchaseMappings } = usePurchaseMappingsContext();
  const { refetchCustomEventDetails } = useCustomEventDetailsContext();

  const {
    formState: { isSubmitting, isSubmitted, isValid },
    handleSubmit,
  } = useFormContext<MappingFieldValues>();

  const details = FIELD_PROPERTY_DETAILS[fieldProperty];

  const options = {
    onSuccess: () => {
      if (field?.dataType === GenericDataType.User) {
        refetchUserMappings();
      }
      if (field?.dataType === GenericDataType.Purchase) {
        refetchPurchaseMappings();
      }
      if (field?.dataType === GenericDataType.CustomEvent) {
        refetchCustomEventDetails();
      }
      dispatch(addPositiveToast(`${details?.labelText} updated`));
      hideModal();
    },
    onError: (e: unknown) => {
      const { response } = e as ApiError;
      const error = response?.data ?? '';
      const errorText = `Error updating ${details?.labelText?.toLowerCase()}. ${error}`;
      dispatch(addNegativeToastWithErrorId(errorText, 'EditPropertyModal', e));
      hideModal();
    },
  };

  const { mutate: updateDescription } = useUpdateDescription(options);
  const { mutate: updateSampleValue } = useUpdateSampleValue(options);

  if (!details || !field) {
    return null;
  }

  const { formFieldId, inputId, labelText, name } = details;

  const onSubmitForm = (data: MappingFieldValues) => {
    const { fieldName, description = '', sampleValue = '' } = data;
    const payload = {
      dataType: field.dataType,
      description,
      fieldName: customEventName ? `${customEventName}.${fieldName}` : fieldName,
      projectId: project.id,
      sampleValue,
    };

    switch (fieldProperty) {
      case FieldProperty.DESCRIPTION:
        return updateDescription(payload);
      case FieldProperty.SAMPLE_VALUE:
        return updateSampleValue(payload);
      default:
        return dispatch(
          addNegativeToastWithErrorId(
            'Error updating unknown field property',
            'EditPropertyModal',
            data,
          ),
        );
    }
  };

  return (
    <Modal id={EDIT_PROPERTY_MODAL_TEXT.modalId} withPortal isModalOpen onClose={hideModal}>
      <ModalHeader onClose={hideModal} autoFocus={false}>
        {`${field[fieldProperty] ? 'Edit' : 'Add'} ${labelText.toLowerCase()}`}
      </ModalHeader>
      <ModalBody>
        <form
          data-test={EDIT_PROPERTY_MODAL_TEXT.formId}
          id={EDIT_PROPERTY_MODAL_TEXT.formId}
          onSubmit={handleSubmit(onSubmitForm)}
        >
          <Body2>
            <span style={{ color: colors.textSecondary }}>Field name: </span>
            {field.fieldName}
          </Body2>
          <ControlledInputField
            autoFocus
            formFieldId={formFieldId}
            inputId={inputId}
            labelText={labelText}
            name={name}
          />
        </form>
      </ModalBody>
      <ModalFooter onClose={hideModal}>
        <Button
          isDisabled={isSubmitted && !isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmitForm)}
          size={BUTTON_SIZES.LARGE}
          type="submit"
          form={EDIT_PROPERTY_MODAL_TEXT.formId}
        >
          {EDIT_PROPERTY_MODAL_TEXT.saveButtonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
